import { call, debounce, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { v4 as uuidv4 } from "uuid";

import ChatFailureType from "@mapmycustomers/shared/enum/chat/ChatFailureType";
import ChatMessageContentType from "@mapmycustomers/shared/enum/chat/ChatMessageContentType";
import ChatMessageRole from "@mapmycustomers/shared/enum/chat/ChatMessageRole";
import ChatMessageSource from "@mapmycustomers/shared/enum/chat/ChatMessageSource";
import ChatProposalApplyOutcome from "@mapmycustomers/shared/enum/chat/ChatProposalApplyOutcome";
import ChatProposalStatus from "@mapmycustomers/shared/enum/chat/ChatProposalStatus";
import EntityType from "@mapmycustomers/shared/enum/EntityType";
import { ChatProposal } from "@mapmycustomers/shared/types/chat/ChatContent";
import ChatMessage, {
  ChatContextEntityRef,
  ChatContextEntityType,
  ChatVoiceMeta,
} from "@mapmycustomers/shared/types/chat/ChatMessage";
import {
  FieldAgentAcceptedProposal,
  FieldAgentApplyResponse,
  FieldAgentApplyResultItem,
  FieldAgentChatResponse,
} from "@mapmycustomers/shared/types/chat/FieldAgentChat";
import Activity from "@mapmycustomers/shared/types/entity/Activity";
import Company from "@mapmycustomers/shared/types/entity/Company";
import Deal from "@mapmycustomers/shared/types/entity/Deal";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import Person from "@mapmycustomers/shared/types/entity/Person";
import { AnyEntityId } from "@mapmycustomers/shared/types/entity/types";
import Organization from "@mapmycustomers/shared/types/Organization";
import { isRecord } from "@mapmycustomers/shared/util/chat/chatWireGuards";
import parseChatResponse, {
  ParsedChatResponse,
} from "@mapmycustomers/shared/util/chat/parseChatResponse";

import { callApi } from "@app/store/api/callApi";
import { initializeApp } from "@app/store/app/actions";
import { getFunnelStages } from "@app/store/deal";
import { getLastActiveEntityPreview } from "@app/store/entityView";
import ActiveEntityPreview from "@app/store/entityView/ActiveEntityPreview";
import { handleError } from "@app/store/errors/actions";
import {
  getBulkEmailSignatureText,
  getMe,
  getOrganizationEmailBoilerplateText,
  getOrganizationId,
  getPosition,
} from "@app/store/iam";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import Iam from "@app/types/Iam";
import { fieldAgentChatAnalytics } from "@app/util/analytic/fieldAgentAnalytics";
import chatConversationStorage from "@app/util/chat/chatConversationStorage";
import getChatFailureType from "@app/util/chat/getChatFailureType";
import proposalAnalyticsProps, {
  applyOutcomeProps,
  resolveCardVariant,
} from "@app/util/chat/proposalAnalyticsProps";
import { toAcceptedItem } from "@app/util/chat/proposalApply";
import resolveProposalBlockedReason from "@app/util/chat/proposalBlocked";
import resolveProposalChangedRecords, {
  groupChangedRecords,
} from "@app/util/chat/proposalChangeNotifications";
import {
  ChatProposalUiState,
  isProposalChecked,
  resolveProposalRowStatus,
} from "@app/util/chat/proposalRowStatus";
import loggingService from "@app/util/logging";

import { sendVoiceMessage } from "../voice/actions";

import {
  appendLocalChatMessage,
  applyChatMessageFeedback,
  applyProposals,
  cancelProposals,
  clearChatConversation,
  discardProposalDraft,
  queueChatMessage,
  restoreChatConversation,
  retryChatMessage,
  saveProposalDraft,
  sendChatMessage,
  setChatMessageFeedback,
  toggleProposalChecked,
} from "./actions";
import { getChatConversationId, getChatMessages, getChatProposalUi } from "./selectors";

// Read at send time rather than when the chat opened: the rep can open the chat, then open a
// record, then type, and the contract's framing is "the record the rep is viewing".
function* selectContextEntity() {
  const preview: ActiveEntityPreview | undefined = yield select(getLastActiveEntityPreview);
  // The last entry is the most recently opened preview. Both halves travel together by
  // construction now — v1105 rejects one without the other.
  //
  // `satisfies` links the preview's entityType union to the wire's here, so a future mismatch
  // is a compile error, not a runtime v1103.
  return preview
    ? ({ id: preview.entityId, entityType: preview.entityType } satisfies ChatContextEntityRef)
    : undefined;
}

// The property object every Send Message event carries. Built once and shared by the success and
// failure branches, the way AIInlineWriter/index.tsx:84 does — all four .failed() call sites in
// this codebase pass the same object their .completed() counterpart got.
interface SendMessageTrackingProps {
  conversationId: number | undefined;
  entityType: ChatContextEntityType | undefined;
  hasContextEntity: boolean;
  hasLocation: boolean;
  inputMode: ChatMessageSource;
  isNewConversation: boolean;
  messageLength: number;
}

// Both Field Agent calls carry the rep's position, and the platform parses both with one
// function, so the header is built in one place: a coordinate order that drifted between the two
// would be silently wrong rather than a visible failure.
const userLocationConfig = (position: GeolocationPosition | undefined) =>
  position && {
    headers: {
      "X-User-Location": JSON.stringify({
        accuracy: position.coords.accuracy,
        // GeoJSON order — longitude first
        coordinates: [position.coords.longitude, position.coords.latitude],
      }),
    },
  };

function* performChatTurn(
  id: ChatMessage["id"],
  text: string,
  contextEntity: ChatContextEntityRef | undefined,
  source: ChatMessageSource,
  voice: ChatVoiceMeta | undefined
) {
  // Seeded before the try so the catch below can still read it, and reassigned once the selects
  // have run. Never the message text itself: no analytics call in this codebase sends prose.
  let trackingProps: SendMessageTrackingProps = {
    conversationId: undefined,
    entityType: contextEntity?.entityType,
    hasContextEntity: !!contextEntity,
    hasLocation: false,
    inputMode: source,
    isNewConversation: true,
    messageLength: text.length,
  };
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const conversationId: number | undefined = yield select(getChatConversationId);
    // The app-wide fix findMe captured; powers "near me" asks. Optional by contract — no fix
    // means the header is omitted and the platform silently degrades to no location awareness.
    const position: GeolocationPosition | undefined = yield select(getPosition);
    trackingProps = {
      ...trackingProps,
      conversationId,
      hasLocation: !!position,
      isNewConversation: conversationId === undefined,
    };
    const response: FieldAgentChatResponse = yield callApi(
      "sendFieldAgentChatMessage",
      orgId,
      {
        ...(contextEntity
          ? {
              contextEntityId: contextEntity.id,
              contextEntityType: contextEntity.entityType,
            }
          : {}),
        ...(conversationId === undefined ? {} : { conversationId }),
        message: text,
        // A dictated message is a rep talking through a visit, which is exactly what a de-brief
        // recaps, so only those ask for de-brief mode. A typed message stays an ordinary turn and
        // comes back as per-record proposal cards.
        ...(voice ? { mode: "debrief" as const } : {}),
      },
      userLocationConfig(position)
    );
    const parsed: ParsedChatResponse = yield call(parseChatResponse, response);
    // The mapper collects rather than logs, because it lives in packages/shared and cannot
    // reach loggingService. This is where those diagnostics surface.
    //
    // The wire messages ride along, not just the warning strings. A warning names the content
    // type it could not draw; working out why needs the payload the agent actually sent, and
    // nothing downstream keeps it — the parsed UNSUPPORTED message carries only its raw content
    // type, never its content. Matched by id rather than by position: the two arrays line up
    // today, but a mapper that ever drops a message instead of replacing it would silently
    // misalign them.
    if (parsed.warnings.length) {
      const unrenderableIds = new Set(
        parsed.messages
          .filter(({ contentType }) => contentType === ChatMessageContentType.UNSUPPORTED)
          .map(({ platformId }) => platformId)
      );
      loggingService.warning("Chat response contained messages we cannot render", {
        messages: response.messages.filter(({ id }) => unrenderableIds.has(id)),
        warnings: parsed.warnings,
      });
    }
    yield put(
      sendChatMessage.success({
        id,
        conversationId: parsed.conversationId,
        messages: parsed.messages,
        suggestions: parsed.suggestions,
      })
    );
    fieldAgentChatAnalytics.completed(["Send Message"], {
      ...trackingProps,
      // The authoritative id, not the one read before the request: the first turn of a
      // conversation has none until the reply carries it back.
      conversationId: parsed.conversationId,
      hasUnrenderableContent: parsed.warnings.length > 0,
      replyContentTypes: parsed.messages.map((message) => message.contentType),
      suggestionCount: parsed.suggestions.length,
    });
  } catch (error) {
    const failureType: ChatFailureType = yield call(getChatFailureType, error);
    yield put(sendChatMessage.failure({ id, failureType }));
    fieldAgentChatAnalytics.failed(["Send Message"], {
      ...trackingProps,
      failureType,
    });
    // Toastless — the message's own FAILED status is the surface for this failure. handleError
    // still runs because it is the only 401 → login() re-auth path; nothing else covers it.
    yield put(handleError({ displayNotification: false, error }));
  }
}

// The one send path, shared by a typed message and a dictated one — only the optional `voice`
// tag differs between them on the wire. `source` never reaches the wire; it is what lets the
// Send Message event say whether the rep typed, dictated, tapped a chip, or retried.
function* sendChatTurn(text: string, source: ChatMessageSource, voice?: ChatVoiceMeta) {
  const id = `c-${uuidv4()}`;
  const createdAt = new Date().toISOString();
  const contextEntity: ChatContextEntityRef | undefined = yield call(selectContextEntity);
  // Optimistic: the rep's own words appear before the round trip, and the reducer's SENDING
  // status is what disables the send button for the duration.
  yield put(queueChatMessage({ id, contextEntity, createdAt, text, ...(voice ? { voice } : {}) }));
  yield call(performChatTurn, id, text, contextEntity, source, voice);
}

export function* onSendChatMessage({ payload }: ReturnType<typeof sendChatMessage.request>) {
  yield call(sendChatTurn, payload.text, payload.source ?? ChatMessageSource.TYPED);
}

// ENG-131: Task 11's voice saga only cancels the recording session on this action — this is
// what actually sends the message, reusing sendChatTurn rather than a second path.
export function* onSendVoiceChatMessage({ payload }: ReturnType<typeof sendVoiceMessage.request>) {
  yield call(sendChatTurn, payload.text, ChatMessageSource.VOICE, {
    durationMs: payload.durationMs,
  });
}

export function* onRetryChatMessage({ payload }: ReturnType<typeof retryChatMessage>) {
  const messages: ChatMessage[] = yield select(getChatMessages);
  const message = messages.find(({ id }) => id === payload.id);
  if (!message || message.role !== ChatMessageRole.USER) {
    return;
  }
  // Re-sends the context recorded on the message, not whatever record happens to be open
  // now, so a retry reproduces the request the rep originally made.
  yield call(
    performChatTurn,
    message.id,
    message.text,
    message.contextEntity,
    ChatMessageSource.RETRY,
    message.voice
  );
}

export function* onSetChatMessageFeedback({
  payload,
}: ReturnType<typeof setChatMessageFeedback.request>) {
  const messages: ChatMessage[] = yield select(getChatMessages);
  const message = messages.find(({ id }) => id === payload.id);
  if (!message || message.role !== ChatMessageRole.ASSISTANT) {
    return;
  }
  // Captured into a plain variable, and BEFORE the optimistic put below, on purpose: Redux
  // runs reducers before notifying saga middleware, so if the optimistic write lived on this
  // same request action (as it originally did), this generator would wake up after the
  // reducer already applied the new vote — a second select here would read that new value
  // back, not the one being replaced. `?? null` because undefined (never voted) and null
  // (explicitly cleared) both mean "no vote", so the revert doesn't need a third state.
  const previousFeedback = message.feedback ?? null;
  yield put(applyChatMessageFeedback({ id: payload.id, feedback: payload.feedback }));
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("setFieldAgentMessageFeedback", orgId, message.platformId, {
      isPositive: payload.feedback,
    });
    yield put(setChatMessageFeedback.success({ id: payload.id }));
  } catch (error) {
    loggingService.error("Failed to record chat message feedback", error);
    yield put(setChatMessageFeedback.failure({ id: payload.id, feedback: previousFeedback }));
    // Both on purpose: loggingService is the Sentry path (handleError has none), and toastless
    // handleError is the only 401 → login() re-auth path. The vote revert stays the UI surface.
    yield put(handleError({ displayNotification: false, error }));
  }
}

// An applied proposal that CREATED a record reports its id alone — the apply response carries no
// body, and `notifyAboutChanges` has no id-only channel for an add: `onAdd` fires only when
// `added` is set, and record-usage counting reads `added.length`. So the record is read back
// before it is announced, which also gets the read-shaped entity — custom fields and access
// status included — rather than the create-shaped one the apply could have returned.
function* notifyAboutCreatedRecord(
  orgId: Organization["id"],
  entityType: ChatContextEntityType,
  entityId: AnyEntityId,
  // Only ever read for a Deal — DealFormFields' association picker can attach an existing
  // Company/Person, and that record's own preview needs a nudge too (see below).
  draft: Record<string, unknown> | undefined
) {
  const options = { includeAccessStatus: true, includeCustomFields: true };
  try {
    switch (entityType) {
      case EntityType.ACTIVITY: {
        const activity: Activity = yield callApi("fetchActivity", orgId, entityId, options);
        yield put(notifyAboutChanges({ added: [activity], entityType }));
        break;
      }
      case EntityType.COMPANY: {
        const company: Company = yield callApi("fetchCompany", orgId, entityId, options);
        yield put(notifyAboutChanges({ added: [company], entityType }));
        break;
      }
      case EntityType.DEAL: {
        const deal: Deal = yield callApi("fetchDeal", orgId, entityId, options);
        yield put(notifyAboutChanges({ added: [deal], entityType }));
        const values = draft?.values;
        const accountId =
          isRecord(values) && typeof values.accountId === "number" ? values.accountId : undefined;
        const contactId =
          isRecord(values) && typeof values.contactId === "number" ? values.contactId : undefined;
        // A stale count, not a stale row: the linked record itself was not created, so this is an
        // update notice, not an add.
        if (accountId !== undefined) {
          yield put(
            notifyAboutChanges({ entityType: EntityType.COMPANY, updatedIds: [accountId] })
          );
        }
        if (contactId !== undefined) {
          yield put(notifyAboutChanges({ entityType: EntityType.PERSON, updatedIds: [contactId] }));
        }
        break;
      }
      case EntityType.PERSON: {
        const person: Person = yield callApi("fetchPerson", orgId, entityId, options);
        yield put(notifyAboutChanges({ added: [person], entityType }));
        break;
      }
      // The never assignment is the real guard: widening ChatContextEntityType fails typecheck
      // here rather than shipping a created record that never announces itself. Same pattern as
      // toAcceptedItem in util/chat/proposalApply.ts. Not thrown — the catch below swallows on
      // purpose, so a throw would only be relabelled as a failed read-back.
      default: {
        const unhandled: never = entityType;
        loggingService.error("No created-record read-back for this entity type", { unhandled });
      }
    }
  } catch (error) {
    // Never rethrown: the card has already settled and the rep's record was written. Losing the
    // refresh leaves a stale list, which a reload fixes; failing here would not un-write anything.
    loggingService.error("Failed to read back a record an applied proposal created", error);
  }
}

// Runs after the card has already settled, and owns its error handling for that reason: `put`
// dispatches synchronously, so a subscribed reducer that throws would otherwise reach
// onApplyProposals's catch and flip an APPLIED message to failed — telling the rep an edit that
// landed did not, and inviting them to retry it.
function* notifyAboutAppliedProposals(
  orgId: Organization["id"],
  proposals: ChatProposal[],
  proposalUi: Record<number, ChatProposalUiState>,
  results: FieldAgentApplyResultItem[]
) {
  try {
    // Only `applied` rows changed anything: a rejected row reports `cancelled`, and a conflicted or
    // expired one never reached the record.
    const appliedIds = new Set(
      results
        .filter(({ outcome }) => outcome === ChatProposalApplyOutcome.APPLIED)
        .map(({ proposalId }) => proposalId)
    );
    const changed = proposals
      .filter(({ proposalId }) => appliedIds.has(proposalId))
      .flatMap(({ action }) => resolveProposalChangedRecords(action));
    for (const { entityType, updatedIds } of groupChangedRecords(changed)) {
      yield put(notifyAboutChanges({ entityType, updatedIds }));
    }
    // Separate from the updates above, and not an alternative to them: logging an activity onto a
    // record both creates the activity and dates the record, so a proposal with a parent reports
    // one of each. A standalone activity has no parent and reports only this.
    for (const result of results) {
      if (
        result.outcome === ChatProposalApplyOutcome.APPLIED &&
        result.createdEntityId !== undefined &&
        result.createdEntityType !== undefined
      ) {
        yield call(
          notifyAboutCreatedRecord,
          orgId,
          result.createdEntityType,
          result.createdEntityId,
          proposalUi[result.proposalId]?.draft
        );
      }
    }
  } catch (error) {
    loggingService.error("Failed to announce the records an applied proposal changed", error);
  }
}

// The message lookup, conversationId check, and pending-and-visible filter that Confirm and
// Cancel both need before they diverge on what accepted/rejected to send.
function* resolveParticipatingProposals(messageId: ChatMessage["id"]) {
  const messages: ChatMessage[] = yield select(getChatMessages);
  const message = messages.find(({ id }) => id === messageId);
  if (
    !message ||
    message.role !== ChatMessageRole.ASSISTANT ||
    (message.contentType !== ChatMessageContentType.ACTION_PROPOSAL &&
      message.contentType !== ChatMessageContentType.DEBRIEF)
  ) {
    return undefined;
  }
  const conversationId: number | undefined = yield select(getChatConversationId);
  if (conversationId === undefined) {
    return undefined;
  }
  const proposalUi: Record<number, ChatProposalUiState> = yield select(getChatProposalUi);
  const now = Date.now();
  // Every row the rep can see, which is also what useProposalCardState hands the card. The card's
  // analytics count against this set, so the saga's half of the same process counts against it too.
  const visible = message.proposals.filter((proposal) => !proposalUi[proposal.proposalId]?.hidden);
  // Only pending, visible rows participate. A lapsed row would only bounce back `expired`,
  // and a terminal row would bounce `stale` — and both would pollute the results.
  const participating = visible.filter(
    (proposal) =>
      resolveProposalRowStatus(proposal, proposalUi[proposal.proposalId], now) ===
      ChatProposalStatus.PENDING
  );
  // The card-level "Expired" footer: nothing left to participate, and at least one visible row
  // lapsed client-side rather than being settled by an apply outcome. Same formula the card uses.
  const expired =
    participating.length === 0 &&
    visible.some((proposal) => {
      const ui = proposalUi[proposal.proposalId];
      return (
        !ui?.statusOverride &&
        resolveProposalRowStatus(proposal, ui, now) === ChatProposalStatus.EXPIRED
      );
    });
  return {
    contentType: message.contentType,
    conversationId,
    expired,
    participating,
    platformId: message.platformId,
    proposals: message.proposals,
    proposalUi,
    visible,
  };
}

interface ParticipatingProposalsContext {
  contentType: ChatMessageContentType.ACTION_PROPOSAL | ChatMessageContentType.DEBRIEF;
  conversationId: number;
  expired: boolean;
  participating: ChatProposal[];
  platformId: number;
  proposals: ChatProposal[];
  proposalUi: Record<number, ChatProposalUiState>;
  visible: ChatProposal[];
}

// Shared by Confirm and Cancel: both settle through applyProposals.success/.failure, one path
// instead of two, so the card's error line and outcome folding never diverge between the two.
// Returns the per-row outcomes on success and undefined on failure — this is the only point the
// outcomes exist, and Confirm's completed event reports a summary of them.
function* settleProposalsApply(
  messageId: ChatMessage["id"],
  conversationId: number,
  proposals: ChatProposal[],
  proposalUi: Record<number, ChatProposalUiState>,
  accepted: FieldAgentAcceptedProposal[],
  rejected: number[]
) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    // Apply is the confirm step, so the platform reads this header for confirm-time position
    // rather than reusing whatever backed the chat turn.
    const position: GeolocationPosition | undefined = yield select(getPosition);
    const response: FieldAgentApplyResponse = yield callApi(
      "applyFieldAgentActions",
      orgId,
      { accepted, conversationId, rejected },
      userLocationConfig(position)
    );
    const notFound = response.results.filter(
      ({ outcome }) => outcome === ChatProposalApplyOutcome.NOT_FOUND
    );
    if (notFound.length) {
      // BE never deletes proposal rows, so not_found means we sent a wrong id or paired it
      // with the wrong conversation — a client bug worth a loud trace, not a card decoration.
      loggingService.error("Apply returned not_found for proposals we rendered", {
        proposalIds: notFound.map(({ proposalId }) => proposalId),
      });
    }
    yield put(applyProposals.success({ messageId, results: response.results }));
    yield call(notifyAboutAppliedProposals, orgId, proposals, proposalUi, response.results);
    return response.results;
  } catch (error) {
    loggingService.error("Failed to apply field agent proposals", error);
    yield put(applyProposals.failure({ messageId }));
    return undefined;
  }
}

export function* onApplyProposals({ payload }: ReturnType<typeof applyProposals.request>) {
  const context: ParticipatingProposalsContext | undefined = yield call(
    resolveParticipatingProposals,
    payload.messageId
  );
  if (!context) {
    // The reducer already flipped this message to inFlight, and nothing else ever clears it — a
    // bare return here leaves the card spinning with no button to click.
    yield put(applyProposals.failure({ messageId: payload.messageId }));
    // The message could not be identified at all, so there is no platformId to attribute this to —
    // the conversation is still selectable even when the message is not, and it is all we can send.
    const unresolvedConversationId: number | undefined = yield select(getChatConversationId);
    fieldAgentChatAnalytics.failed(["Card"], {
      action: "accept",
      conversationId: unresolvedConversationId,
      reason: "unresolved",
    });
    return;
  }
  const {
    contentType,
    conversationId,
    expired,
    participating,
    platformId,
    proposals,
    proposalUi,
    visible,
  } = context;
  // Read once per apply, not per proposal: the serializer appends them to every send_email body.
  const boilerplateText: string = yield select(getOrganizationEmailBoilerplateText);
  const signatureText: string = yield select(getBulkEmailSignatureText);
  const funnelStages: Record<Funnel["id"], Stage[]> = yield select(getFunnelStages);
  const accepted = participating
    .filter(
      (proposal) =>
        isProposalChecked(proposalUi[proposal.proposalId]) &&
        !resolveProposalBlockedReason(
          proposal.action,
          proposalUi[proposal.proposalId]?.draft,
          funnelStages
        )
    )
    .map((proposal) =>
      toAcceptedItem(proposal, proposalUi[proposal.proposalId]?.draft, {
        boilerplateText,
        signatureText,
      })
    );
  // Confirm rejects nothing: unchecking leaves a row pending, which expires and can be re-asked.
  // A wrongly rejected row settles cancelled instead, which is terminal — no retry, ever.
  const rejected: number[] = [];
  // The same keys, in the same meaning, that the card sends on its Accept click, so the rep's
  // decision and this outcome join on one shape.
  const trackingProps = {
    action: "accept",
    // The same condition the card renders on: a de-brief always draws the sectioned list.
    cardVariant: resolveCardVariant(contentType, visible),
    // Checked, unblocked and pending — exactly the rows `accepted` carries, which is also how the
    // card counts its own checkedCount.
    checkedCount: accepted.length,
    conversationId,
    expired,
    pendingCount: participating.length,
    platformId,
    // Always zero on this path — Confirm rejects nothing. Sent anyway so both actions emit one
    // key set under this event name rather than two schemas a warehouse has to union.
    rejectedCount: rejected.length,
    // Counted over the visible rows, not the pending subset: the rep saw all of them, and the
    // card's own click event uses the same denominator.
    ...proposalAnalyticsProps(visible),
  };
  // After the resolve, not at saga entry: platformId and the counts come from the context, and an
  // initiated without them could not be joined to the terminal that follows it.
  fieldAgentChatAnalytics.initiated(["Card"], trackingProps);
  if (accepted.length === 0 && rejected.length === 0) {
    // Reachable at the expiry boundary: every row lapsed after the button went live. Same reason
    // as above — resolve the in-flight state rather than returning into it.
    yield put(applyProposals.failure({ messageId: payload.messageId }));
    fieldAgentChatAnalytics.failed(["Card"], {
      ...trackingProps,
      reason: "nothingToApply",
    });
    return;
  }
  const results: FieldAgentApplyResultItem[] | undefined = yield call(
    settleProposalsApply,
    payload.messageId,
    conversationId,
    proposals,
    proposalUi,
    accepted,
    rejected
  );
  if (results) {
    fieldAgentChatAnalytics.completed(
      ["Card"],
      applyOutcomeProps(trackingProps, results, [ChatProposalApplyOutcome.APPLIED])
    );
  } else {
    fieldAgentChatAnalytics.failed(["Card"], {
      ...trackingProps,
      reason: "applyFailed",
    });
  }
}

export function* onCancelProposals({ payload }: ReturnType<typeof cancelProposals>) {
  const context: ParticipatingProposalsContext | undefined = yield call(
    resolveParticipatingProposals,
    payload.messageId
  );
  if (!context) {
    yield put(applyProposals.failure({ messageId: payload.messageId }));
    const unresolvedConversationId: number | undefined = yield select(getChatConversationId);
    fieldAgentChatAnalytics.failed(["Card"], {
      action: "discard",
      conversationId: unresolvedConversationId,
      reason: "unresolved",
    });
    return;
  }
  const {
    contentType,
    conversationId,
    expired,
    participating,
    platformId,
    proposals,
    proposalUi,
    visible,
  } = context;
  const accepted: FieldAgentAcceptedProposal[] = [];
  const rejected = participating.map(({ proposalId }) => proposalId);
  // The same key set the Accept path emits, so one event name carries one schema. Both counts are
  // literally what this request sent: a discard accepts nothing, and it rejects every participating
  // row regardless of which checkboxes were ticked.
  const trackingProps = {
    action: "discard",
    cardVariant: resolveCardVariant(contentType, visible),
    checkedCount: accepted.length,
    conversationId,
    expired,
    pendingCount: participating.length,
    platformId,
    rejectedCount: rejected.length,
    ...proposalAnalyticsProps(visible),
  };
  fieldAgentChatAnalytics.initiated(["Card"], trackingProps);
  if (accepted.length === 0 && rejected.length === 0) {
    // Reachable at the expiry boundary, same as Confirm's guard above.
    yield put(applyProposals.failure({ messageId: payload.messageId }));
    fieldAgentChatAnalytics.failed(["Card"], {
      ...trackingProps,
      reason: "nothingToApply",
    });
    return;
  }
  // Never applyProposals.request: sagas.ts's takeEvery on that action would also launch
  // onApplyProposals, racing a real accept against this reject on the same card.
  const results: FieldAgentApplyResultItem[] | undefined = yield call(
    settleProposalsApply,
    payload.messageId,
    conversationId,
    proposals,
    proposalUi,
    accepted,
    rejected
  );
  if (results) {
    fieldAgentChatAnalytics.completed(
      ["Card"],
      applyOutcomeProps(trackingProps, results, [
        ChatProposalApplyOutcome.CANCELLED,
        ChatProposalApplyOutcome.EXPIRED,
      ])
    );
  } else {
    fieldAgentChatAnalytics.failed(["Card"], {
      ...trackingProps,
      reason: "applyFailed",
    });
  }
}

function* onPersistChatConversation() {
  const me: Iam = yield select(getMe);

  function* persist() {
    const conversationId: number | undefined = yield select(getChatConversationId);
    const messages: ChatMessage[] = yield select(getChatMessages);
    const proposalUi: Record<number, ChatProposalUiState> = yield select(getChatProposalUi);
    yield call(chatConversationStorage.setState, {
      conversationId,
      messages,
      proposalUi,
      userId: me.id,
    });
  }

  yield takeEvery(
    [
      appendLocalChatMessage,
      applyChatMessageFeedback,
      applyProposals.success,
      clearChatConversation,
      queueChatMessage,
      restoreChatConversation,
      retryChatMessage,
      sendChatMessage.failure,
      sendChatMessage.success,
      setChatMessageFeedback.failure,
      toggleProposalChecked,
    ],
    persist
  );
  // A standalone proposal body is always live, so it commits a draft per change. This write walks
  // the whole transcript and JSON.stringifies it synchronously inside the dispatch, so it waits
  // for the typing to settle; the draft itself still reaches Redux immediately.
  yield debounce(300, [discardProposalDraft, saveProposalDraft], persist);
}

// Runs after initializeApp has established who is logged in — a transcript cannot hydrate at
// module-eval the way geometry does, because it belongs to a specific user. Forking the
// persistence watcher from HERE, after the restore, is what makes the ordering structural:
// there is no window in which a write could erase a thread we have not read yet.
export function* onHydrateChatConversation() {
  const me: Iam = yield select(getMe);
  const stored: ReturnType<typeof chatConversationStorage.getState> = yield call(
    chatConversationStorage.getState,
    me.id
  );
  if (stored) {
    yield put(
      restoreChatConversation({
        conversationId: stored.conversationId,
        messages: stored.messages,
        proposalUi: stored.proposalUi,
      })
    );
  }
  yield call(onPersistChatConversation);
}

export function* chatConversationSagas() {
  yield takeEvery(sendChatMessage.request, onSendChatMessage);
  // A second, independent watcher on the same action Task 11's voice saga already takes — that
  // one cancels the recording session, this one is what actually sends the message.
  yield takeEvery(sendVoiceMessage.request, onSendVoiceChatMessage);
  yield takeEvery(retryChatMessage, onRetryChatMessage);
  yield takeEvery(setChatMessageFeedback.request, onSetChatMessageFeedback);
  // takeEvery, not takeLatest, for both: two different proposal cards may apply or cancel
  // concurrently, and the reducer keys in-flight state by messageId so they never collide.
  yield takeEvery(applyProposals.request, onApplyProposals);
  yield takeEvery(cancelProposals, onCancelProposals);
  // takeLatest, not takeEvery: a same-tab sign-out + sign-in redispatches initializeApp.success,
  // and takeEvery would leave the previous cycle's hydrate task (and the persist watcher it
  // forked, still closed over the previous user's id) running forever alongside the new one —
  // two watchers racing to write under two different userIds. takeLatest cancels that whole
  // forked subtree before starting the next cycle.
  yield takeLatest(initializeApp.success, onHydrateChatConversation);
  // Registered here, independent of the hydrate/persist-watcher lifecycle above, because that
  // lifecycle does not exist yet the first time this matters: store/app/sagas.ts dispatches
  // clearChatConversation on a user switch strictly before initializeApp.success (which is what
  // forks the persist watcher). Without this, the Redux slice resets but sessionStorage still
  // holds the previous user's transcript until the next user happens to trigger a persisted
  // action of their own.
  yield takeEvery(clearChatConversation, function* () {
    yield call(chatConversationStorage.clear);
  });
}
